import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  FileText, 
  Calendar, 
  Printer, 
  Download, 
  User,
  CreditCard,
  Banknote,
  Clock,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  BarChart,
  AlertTriangle,
  DollarSign,
  Percent,
  List,
  Table,
  RotateCcw,
  RotateCw,
  Settings,
} from 'lucide-react';
import CurrencyDisplay from '../../components/CurrencyDisplay';
import useElementToPdf from '../../utils/useElementToPdf';
import SalaryTiersModal from './detailed_reports/SalaryTiersModal';
import PrintOptionsModal from './detailed_reports/PrintOptionsModal';

const DetailedReports = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { user, isAdmin } = useAuth();
  const printRef = useRef();
  
  const [reportType, setReportType] = useState('daily'); // daily, weekly, monthly
  const [reportMode, setReportMode] = useState('detailed'); // detailed, summary
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to yesterday for daily reports
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Default to last month
    const today = new Date();
    return today.getMonth() === 0 ? 12 : today.getMonth();
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const today = new Date();
    return today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
  });
  const [weekStartDay, setWeekStartDay] = useState(5); // 5 = Saturday
  const [selectedBarbers, setSelectedBarbers] = useState([]); // Multiple barbers selection
  const [barbers, setBarbers] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  
  // Print settings
  const [printOrientation, setPrintOrientation] = useState('portrait'); // portrait or landscape
  const [printLanguage, setPrintLanguage] = useState(language);
  const [bilingualPrint, setBilingualPrint] = useState(false);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  
  const isArabic = language === 'ar';
  
  // Salary settings
  const [salaryPercentage, setSalaryPercentage] = useState('');
  const [salaryTiers, setSalaryTiers] = useState([]);
  const [showTiersModal, setShowTiersModal] = useState(false);
  const [newTier, setNewTier] = useState({ revenue_from: '', revenue_to: '', percentage: '' });
  const [canViewSalaries, setCanViewSalaries] = useState(false);
  
  // Warning state
  const [warningMessage, setWarningMessage] = useState(null);

  useEffect(() => {
    if (isAdmin()) {
      fetchBarbers();
    }
    // Check salary tiers permission
    checkSalaryPermission();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSalaryPermission = async () => {
    try {
      const res = await api.get('/detailed-reports/salary-tiers');
      setCanViewSalaries(true);
      setSalaryTiers(res.data.tiers || []);
    } catch (error) {
      // User doesn't have permission
      setCanViewSalaries(false);
      setSalaryTiers([]);
    }
  };

  const fetchBarbers = async () => {
    try {
      const res = await api.get('/barbers/all');
      setBarbers(res.data);
    } catch (error) {
      console.error('Failed to fetch barbers:', error);
    }
  };

  const fetchSalaryTiers = async () => {
    if (!canViewSalaries) return;
    try {
      const res = await api.get('/detailed-reports/salary-tiers');
      setSalaryTiers(res.data.tiers || []);
    } catch (error) {
      console.error('Failed to fetch salary tiers:', error);
    }
  };

  const saveSalaryTiers = async () => {
    try {
      await api.put('/detailed-reports/salary-tiers', { tiers: salaryTiers });
      toast.success(language === 'ar' ? 'تم حفظ شرائح الرواتب' : 'Salary tiers saved');
      setShowTiersModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل حفظ الشرائح' : 'Failed to save tiers'));
    }
  };

  const addTier = () => {
    if (salaryTiers.length >= 4) {
      toast.error(language === 'ar' ? 'الحد الأقصى 4 شرائح' : 'Maximum 4 tiers allowed');
      return;
    }
    if (!newTier.revenue_from || !newTier.percentage) {
      toast.error(language === 'ar' ? 'حقول "من" و "النسبة" مطلوبة' : '"From" and "Percentage" are required');
      return;
    }
    // If revenue_to is empty, set to 999999 (means "and above")
    const revenueToValue = newTier.revenue_to ? parseFloat(newTier.revenue_to) : 999999;
    
    setSalaryTiers([...salaryTiers, {
      revenue_from: parseFloat(newTier.revenue_from),
      revenue_to: revenueToValue,
      percentage: parseFloat(newTier.percentage)
    }]);
    setNewTier({ revenue_from: '', revenue_to: '', percentage: '' });
  };

  const removeTier = (index) => {
    setSalaryTiers(salaryTiers.filter((_, i) => i !== index));
  };

  // Toggle barber selection for multi-select
  const toggleBarberSelection = (barberId) => {
    if (selectedBarbers.includes(barberId)) {
      setSelectedBarbers(selectedBarbers.filter(id => id !== barberId));
    } else {
      setSelectedBarbers([...selectedBarbers, barberId]);
    }
  };

  // Select all barbers
  const selectAllBarbers = () => {
    if (selectedBarbers.length === barbers.length) {
      setSelectedBarbers([]);
    } else {
      setSelectedBarbers(barbers.map(b => b.id));
    }
  };

  const fetchReport = async () => {
    console.log('fetchReport called');
    console.log('reportType:', reportType);
    console.log('selectedDate:', selectedDate);
    console.log('isAdmin:', isAdmin());
    
    setLoading(true);
    setReportData(null);
    setWarningMessage(null);

    try {
      let url = '';
      const params = new URLSearchParams();
      params.append('language', language);
      params.append('report_mode', reportMode);

      // Determine barber_id parameter
      if (isAdmin()) {
        if (selectedBarbers.length === 0) {
          // All barbers (entire salon)
          params.append('barber_id', 'all');
        } else if (selectedBarbers.length === 1) {
          // Single barber
          params.append('barber_id', selectedBarbers[0]);
        } else {
          // Multiple barbers - join with comma
          params.append('barber_ids', selectedBarbers.join(','));
        }
      }
      // Barbers can only see their own reports - no barber_id param needed

      if (reportType === 'daily') {
        url = `/detailed-reports/daily?date=${selectedDate}&${params.toString()}`;
      } else if (reportType === 'weekly') {
        params.append('date', selectedDate);
        if (isAdmin()) {
          params.append('week_start_day', weekStartDay);
        }
        url = `/detailed-reports/weekly?${params.toString()}`;
      } else if (reportType === 'monthly') {
        params.append('year', selectedYear);
        params.append('month', selectedMonth);
        // Add salary percentage for individual barber monthly reports
        if (selectedBarbers.length === 1 && salaryPercentage) {
          params.append('salary_percentage', salaryPercentage);
        }
        url = `/detailed-reports/monthly?${params.toString()}`;
      }

      const res = await api.get(url);
      setReportData(res.data);
      
      // Check for warning
      if (res.data.warning) {
        setWarningMessage(res.data.warning[language] || res.data.warning.ar);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل تحميل التقرير' : 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  };

  // Print styles for A4
  const getPageStyle = () => `
    @page {
      size: A4 ${printOrientation};
      margin: 15mm;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        direction: ${bilingualPrint ? 'rtl' : (printLanguage === 'ar' ? 'rtl' : 'ltr')};
      }
      .no-print {
        display: none !important;
      }
    }
  `;

  const handlePrint = useElementToPdf({
    contentRef: printRef,
    documentTitle: `report_${reportType}_${selectedDate}`,
    pageStyle: getPageStyle(),
    onAfterPrint: async () => {
      // Log print action
      try {
        await api.post('/detailed-reports/log-print', {
          report_type: reportType,
          barber_id: selectedBarbers.length === 1 ? selectedBarbers[0] : (selectedBarbers.length > 1 ? selectedBarbers.join(',') : 'all'),
          date_from: reportType === 'monthly' ? `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01` : selectedDate,
          date_to: reportType === 'monthly' ? `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-28` : selectedDate
        });
      } catch (e) {
        console.log('Failed to log print');
      }
    }
  });
  
  const triggerPrint = () => {
    setShowPrintOptions(false);
    setTimeout(() => handlePrint(), 100);
  };

  const toggleDay = (dayKey) => {
    setExpandedDays(prev => ({
      ...prev,
      [dayKey]: !prev[dayKey]
    }));
  };

  const weekDays = [
    { value: 0, label: language === 'ar' ? 'الإثنين' : 'Monday' },
    { value: 1, label: language === 'ar' ? 'الثلاثاء' : 'Tuesday' },
    { value: 2, label: language === 'ar' ? 'الأربعاء' : 'Wednesday' },
    { value: 3, label: language === 'ar' ? 'الخميس' : 'Thursday' },
    { value: 4, label: language === 'ar' ? 'الجمعة' : 'Friday' },
    { value: 5, label: language === 'ar' ? 'السبت' : 'Saturday' },
    { value: 6, label: language === 'ar' ? 'الأحد' : 'Sunday' }
  ];

  return (
    <div className="min-h-screen bg-background p-4 md:p-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {language === 'ar' ? 'التقارير التفصيلية' : 'Detailed Reports'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'ar' ? 'تقارير الإيرادات اليومية والأسبوعية والشهرية' : 'Daily, weekly, and monthly revenue reports'}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card border border-white/5 rounded-xl p-6 space-y-4">
          {/* Report Type */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setReportType('daily')}
              variant={reportType === 'daily' ? 'default' : 'outline'}
              className="gap-2"
            >
              <Calendar className="w-4 h-4" />
              {language === 'ar' ? 'يومي' : 'Daily'}
            </Button>
            <Button
              onClick={() => setReportType('weekly')}
              variant={reportType === 'weekly' ? 'default' : 'outline'}
              className="gap-2"
            >
              <BarChart className="w-4 h-4" />
              {language === 'ar' ? 'أسبوعي' : 'Weekly'}
            </Button>
            <Button
              onClick={() => setReportType('monthly')}
              variant={reportType === 'monthly' ? 'default' : 'outline'}
              className="gap-2"
            >
              <TrendingUp className="w-4 h-4" />
              {language === 'ar' ? 'شهري' : 'Monthly'}
            </Button>
          </div>

          {/* Report Mode (Summary/Detailed) - Only for weekly and monthly */}
          {(reportType === 'weekly' || reportType === 'monthly') && (
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setReportMode('detailed')}
                variant={reportMode === 'detailed' ? 'default' : 'outline'}
                size="sm"
                className="gap-2"
              >
                <List className="w-4 h-4" />
                {language === 'ar' ? 'مفصل' : 'Detailed'}
              </Button>
              <Button
                onClick={() => setReportMode('summary')}
                variant={reportMode === 'summary' ? 'default' : 'outline'}
                size="sm"
                className="gap-2"
              >
                <Table className="w-4 h-4" />
                {language === 'ar' ? 'ملخص' : 'Summary'}
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date Selection */}
            {reportType === 'monthly' ? (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {language === 'ar' ? 'السنة' : 'Year'}
                  </label>
                  <Input
                    type="number"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    min={2020}
                    max={2030}
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {language === 'ar' ? 'الشهر' : 'Month'}
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="w-full h-10 px-3 rounded-md bg-secondary/50 border border-white/10"
                  >
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {language === 'ar' 
                          ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'][i]
                          : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][i]
                        }
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-2">
                  {language === 'ar' ? 'التاريخ' : 'Date'}
                </label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="bg-secondary/50 border-white/10"
                />
              </div>
            )}

            {/* Week Start Day (Admin only for weekly) */}
            {reportType === 'weekly' && isAdmin() && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  {language === 'ar' ? 'بداية الأسبوع' : 'Week Start'}
                </label>
                <select
                  value={weekStartDay}
                  onChange={(e) => setWeekStartDay(parseInt(e.target.value))}
                  className="w-full h-10 px-3 rounded-md bg-secondary/50 border border-white/10"
                >
                  {weekDays.map((day) => (
                    <option key={day.value} value={day.value}>{day.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Print Orientation (available to everyone — user can choose before viewing) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'ar' ? 'اتجاه الصفحة (A4)' : 'Page Orientation (A4)'}
              </label>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPrintOrientation('portrait')}
                  variant={printOrientation === 'portrait' ? 'default' : 'outline'}
                  size="sm"
                  className="gap-1"
                  data-testid="orientation-portrait-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                  {language === 'ar' ? 'عمودي' : 'Portrait'}
                </Button>
                <Button
                  onClick={() => setPrintOrientation('landscape')}
                  variant={printOrientation === 'landscape' ? 'default' : 'outline'}
                  size="sm"
                  className="gap-1"
                  data-testid="orientation-landscape-btn"
                >
                  <RotateCw className="w-4 h-4" />
                  {language === 'ar' ? 'أفقي' : 'Landscape'}
                </Button>
              </div>
            </div>
          </div>

          {/* Barber Selection (Admin only) - Multiple selection */}
          {isAdmin() && (
            <div className="border border-white/10 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="font-medium">
                  <User className="w-4 h-4 inline mr-1" />
                  {language === 'ar' ? 'اختيار الحلاقين' : 'Select Barbers'}
                </label>
                <Button
                  onClick={selectAllBarbers}
                  variant="outline"
                  size="sm"
                >
                  {selectedBarbers.length === barbers.length 
                    ? (language === 'ar' ? 'إلغاء الكل' : 'Deselect All')
                    : (language === 'ar' ? 'اختيار الكل' : 'Select All')}
                </Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {barbers.map((barber) => (
                  <label 
                    key={barber.id} 
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border transition-colors ${
                      selectedBarbers.includes(barber.id) 
                        ? 'bg-primary/20 border-primary' 
                        : 'bg-secondary/30 border-white/10 hover:bg-secondary/50'
                    }`}
                  >
                    <Checkbox
                      checked={selectedBarbers.includes(barber.id)}
                      onCheckedChange={() => toggleBarberSelection(barber.id)}
                    />
                    <span className="text-sm">{barber.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {selectedBarbers.length === 0 
                  ? (language === 'ar' ? 'لم يتم اختيار أي حلاق = الصالون كامل' : 'No barber selected = Entire Salon')
                  : (language === 'ar' ? `تم اختيار ${selectedBarbers.length} حلاق` : `${selectedBarbers.length} barber(s) selected`)}
              </p>
            </div>
          )}

          {/* Salary Percentage (for users with salary_tiers permission, for individual barber monthly report) */}
          {canViewSalaries && reportType === 'monthly' && selectedBarbers.length === 1 && (
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium flex items-center gap-1">
                <Percent className="w-4 h-4" />
                {language === 'ar' ? 'نسبة الراتب %' : 'Salary %'}
              </label>
              <Input
                type="number"
                value={salaryPercentage}
                onChange={(e) => setSalaryPercentage(e.target.value)}
                placeholder={language === 'ar' ? 'مثال: 30' : 'e.g. 30'}
                min="0"
                max="100"
                className="bg-secondary/50 border-white/10 w-32"
              />
            </div>
          )}

          {/* Salary Tiers Button (for users with salary_tiers permission, for salon-wide monthly report) */}
          {canViewSalaries && reportType === 'monthly' && selectedBarbers.length !== 1 && (
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => setShowTiersModal(true)} 
                variant="outline" 
                className="gap-2"
              >
                <Settings className="w-4 h-4" />
                {language === 'ar' ? 'إدارة شرائح الرواتب' : 'Manage Salary Tiers'}
              </Button>
              {salaryTiers.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  ({salaryTiers.length} {language === 'ar' ? 'شرائح' : 'tiers'})
                </span>
              )}
            </div>
          )}

          <Button onClick={() => { console.log('Button clicked!'); fetchReport(); }} disabled={loading} className="gap-2" data-testid="view-report-btn">
            {loading ? (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
            ) : (
              <FileText className="w-4 h-4" />
            )}
            {language === 'ar' ? 'عرض التقرير' : 'View Report'}
          </Button>
        </div>

        {/* Warning Message */}
        {warningMessage && (
          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0" />
            <p className="text-yellow-500 font-medium">{warningMessage}</p>
          </div>
        )}

        {/* Report Display */}
        {reportData && (
          <div className="space-y-4">
            {/* Print Options Modal */}
            <PrintOptionsModal
              open={showPrintOptions}
              isArabic={isArabic}
              orientation={printOrientation}
              onOrientationChange={setPrintOrientation}
              language={printLanguage}
              onLanguageChange={setPrintLanguage}
              bilingual={bilingualPrint}
              onBilingualChange={setBilingualPrint}
              onConfirm={triggerPrint}
              onClose={() => setShowPrintOptions(false)}
            />
            
            {/* Print Button */}
            <div className="flex justify-end">
              <Button onClick={() => setShowPrintOptions(true)} className="gap-2 bg-green-600 hover:bg-green-700">
                <Printer className="w-4 h-4" />
                {language === 'ar' ? 'طباعة التقرير' : 'Print Report'}
              </Button>
            </div>

            {/* Printable Area - displayed at A4 size on screen.
                Default A4 portrait, switches to landscape if user changed orientation. */}
            <div className="w-full overflow-x-auto flex justify-center">
              <div
                ref={printRef}
                data-testid="report-a4-page"
                className="bg-card border border-white/5 rounded-xl p-6 print:bg-white print:text-black mx-auto"
                style={{
                  width: printOrientation === 'landscape' ? '297mm' : '210mm',
                  minHeight: printOrientation === 'landscape' ? '210mm' : '297mm',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}
              >
              {/* Report Header with Logo */}
              <div className="text-center mb-6 pb-4 border-b border-white/10 print:border-gray-300">
                <img 
                  src="/logo.png" 
                  alt="KE Gents Saloon" 
                  className="w-20 h-20 mx-auto mb-3 object-contain"
                />
                <h2 className="text-2xl font-bold mb-2">KE Gents Saloon</h2>
                <h3 className="text-lg font-semibold">
                  {reportType === 'daily' && (language === 'ar' ? 'تقرير يومي' : 'Daily Report')}
                  {reportType === 'weekly' && (language === 'ar' ? `تقرير أسبوعي (${reportMode === 'summary' ? 'ملخص' : 'مفصل'})` : `Weekly Report (${reportMode})`)}
                  {reportType === 'monthly' && (language === 'ar' ? `تقرير شهري (${reportMode === 'summary' ? 'ملخص' : 'مفصل'})` : `Monthly Report (${reportMode})`)}
                </h3>
                <p className="text-muted-foreground print:text-gray-600">
                  <User className="w-4 h-4 inline mr-1" />
                  {reportData.barber_name}
                </p>
                {reportType === 'daily' && (
                  <p className="text-muted-foreground print:text-gray-600">
                    {reportData.day_name} - {reportData.date}
                  </p>
                )}
                {reportType === 'weekly' && (
                  <p className="text-muted-foreground print:text-gray-600">
                    {reportData.week_start} → {reportData.week_end}
                  </p>
                )}
                {reportType === 'monthly' && (
                  <p className="text-muted-foreground print:text-gray-600">
                    {reportData.month_name} {reportData.year}
                  </p>
                )}
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-green-500/10 rounded-lg p-4 text-center print:border print:border-gray-300">
                  <Banknote className="w-6 h-6 mx-auto mb-2 text-green-500" />
                  <div className="text-xs text-muted-foreground">{language === 'ar' ? 'مجموع الكاش' : 'Total Cash'}</div>
                  <div className="text-xl font-bold text-green-500">
                    <CurrencyDisplay amount={reportData.summary.total_cash} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {reportData.summary.cash_count} {language === 'ar' ? 'خدمة' : 'services'}
                  </div>
                </div>

                <div className="bg-blue-500/10 rounded-lg p-4 text-center print:border print:border-gray-300">
                  <CreditCard className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                  <div className="text-xs text-muted-foreground">{language === 'ar' ? 'مجموع المكينة' : 'Total Card'}</div>
                  <div className="text-xl font-bold text-blue-500">
                    <CurrencyDisplay amount={reportData.summary.total_card} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {reportData.summary.card_count} {language === 'ar' ? 'خدمة' : 'services'}
                  </div>
                </div>

                <div className="bg-primary/10 rounded-lg p-4 text-center print:border print:border-gray-300">
                  <TrendingUp className="w-6 h-6 mx-auto mb-2 text-primary" />
                  <div className="text-xs text-muted-foreground">{language === 'ar' ? 'المجموع الكلي' : 'Total'}</div>
                  <div className="text-xl font-bold text-primary">
                    <CurrencyDisplay amount={reportData.summary.total_amount} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {reportData.summary.total_count} {language === 'ar' ? 'خدمة' : 'services'}
                  </div>
                </div>

                <div className="bg-orange-500/10 rounded-lg p-4 text-center print:border print:border-gray-300">
                  <BarChart className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                  <div className="text-xs text-muted-foreground">{language === 'ar' ? 'متوسط الخدمة' : 'Avg Service'}</div>
                  <div className="text-xl font-bold text-orange-500">
                    <CurrencyDisplay amount={reportData.summary.average_service} />
                  </div>
                </div>

                {/* Working Days & Average Daily (for weekly/monthly) */}
                {(reportType === 'weekly' || reportType === 'monthly') && reportData.summary.working_days !== undefined && (
                  <>
                    <div className="bg-cyan-500/10 rounded-lg p-4 text-center print:border print:border-gray-300">
                      <Calendar className="w-6 h-6 mx-auto mb-2 text-cyan-500" />
                      <div className="text-xs text-muted-foreground">{language === 'ar' ? 'أيام العمل' : 'Working Days'}</div>
                      <div className="text-xl font-bold text-cyan-500">{reportData.summary.working_days}</div>
                    </div>
                    <div className="bg-amber-500/10 rounded-lg p-4 text-center print:border print:border-gray-300">
                      <TrendingUp className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                      <div className="text-xs text-muted-foreground">{language === 'ar' ? 'متوسط يومي' : 'Daily Avg'}</div>
                      <div className="text-xl font-bold text-amber-500">
                        <CurrencyDisplay amount={reportData.summary.average_daily} />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Daily Report - Transactions Table */}
              {reportType === 'daily' && reportData.transactions && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 print:border-gray-300">
                        <th className="text-right py-2 px-3">#</th>
                        <th className="text-right py-2 px-3">{language === 'ar' ? 'الوقت' : 'Time'}</th>
                        <th className="text-right py-2 px-3">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                        <th className="text-right py-2 px-3">{language === 'ar' ? 'الدفع' : 'Payment'}</th>
                        <th className="text-right py-2 px-3">{language === 'ar' ? 'ملاحظات' : 'Notes'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.transactions.map((tx, index) => (
                        <tr key={`tx-${tx.time}-${tx.amount}-${index}`} className="border-b border-white/5 print:border-gray-200">
                          <td className="py-2 px-3">{index + 1}</td>
                          <td className="py-2 px-3">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {tx.time}
                          </td>
                          <td className="py-2 px-3 font-semibold">
                            <CurrencyDisplay amount={tx.amount} />
                          </td>
                          <td className="py-2 px-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              tx.payment_method_en === 'cash' 
                                ? 'bg-green-500/20 text-green-500' 
                                : 'bg-blue-500/20 text-blue-500'
                            }`}>
                              {tx.payment_method}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-muted-foreground">{tx.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Daily Report — Approved Leaves / Permissions */}
              {reportType === 'daily' && reportData.leaves && reportData.leaves.length > 0 && (
                <div className="mt-4 p-4 bg-amber-500/10 rounded-lg print:border print:border-amber-300">
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    {language === 'ar' ? 'إجازات / أذونات معتمدة لهذا اليوم' : 'Approved Leaves / Permissions for this date'}
                  </h4>
                  <ul className="text-sm space-y-1">
                    {reportData.leaves.map((lv) => (
                      <li key={lv.id} className="flex items-center justify-between gap-2 border-b border-white/5 last:border-b-0 pb-1">
                        <span className="font-medium">{lv.barber_name || '-'}</span>
                        <span className="text-amber-500">
                          {lv.type === 'leave'
                            ? (language === 'ar' ? 'إجازة (يوم كامل)' : 'Leave (full day)')
                            : (language === 'ar' ? `إذن ${lv.hours || 0} ساعات` : `Permission ${lv.hours || 0}h`)}
                        </span>
                        <span className="text-xs text-muted-foreground">{lv.reason || '-'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Weekly Report - Daily Breakdown */}
              {reportType === 'weekly' && reportData.daily_breakdown && (
                <div className="space-y-2">
                  {reportMode === 'detailed' ? (
                    // Detailed view with expandable days
                    reportData.daily_breakdown.map((day) => (
                      <div key={day.date} className="border border-white/10 rounded-lg overflow-hidden print:border-gray-300">
                        <div 
                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/5"
                          onClick={() => toggleDay(day.date)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-semibold">{day.day_name}</span>
                            <span className="text-sm text-muted-foreground">{day.date}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-green-500">
                              <CurrencyDisplay amount={day.cash_total} />
                            </span>
                            <span className="text-blue-500">
                              <CurrencyDisplay amount={day.card_total} />
                            </span>
                            <span className="font-bold">
                              <CurrencyDisplay amount={day.total} />
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({day.count})
                            </span>
                            {day.revenues && day.revenues.length > 0 && (
                              expandedDays[day.date] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                            )}
                          </div>
                        </div>
                        {expandedDays[day.date] && day.revenues && day.revenues.length > 0 && (
                          <div className="bg-secondary/30 p-3 print:bg-gray-100">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-white/10">
                                  <th className="text-right py-1">{language === 'ar' ? 'الوقت' : 'Time'}</th>
                                  <th className="text-right py-1">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                                  <th className="text-right py-1">{language === 'ar' ? 'الدفع' : 'Payment'}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {day.revenues.map((r, i) => (
                                  <tr key={`${day.date}-rev-${r.receipt_time}-${r.amount}-${i}`}>
                                    <td className="py-1">{r.receipt_time || '-'}</td>
                                    <td className="py-1"><CurrencyDisplay amount={r.amount} /></td>
                                    <td className="py-1">{r.payment_method === 'cash' ? 'كاش' : 'مكينة'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    // Summary view - simple table
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10 print:border-gray-300">
                            <th className="text-right py-2 px-3">{language === 'ar' ? 'اليوم' : 'Day'}</th>
                            <th className="text-right py-2 px-3">{language === 'ar' ? 'التاريخ' : 'Date'}</th>
                            <th className="text-right py-2 px-3">{language === 'ar' ? 'كاش' : 'Cash'}</th>
                            <th className="text-right py-2 px-3">{language === 'ar' ? 'مكينة' : 'Card'}</th>
                            <th className="text-right py-2 px-3">{language === 'ar' ? 'الإجمالي' : 'Total'}</th>
                            <th className="text-right py-2 px-3">{language === 'ar' ? 'العدد' : 'Count'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.daily_breakdown.map((day) => (
                            <tr key={day.date} className="border-b border-white/5 print:border-gray-200">
                              <td className="py-2 px-3 font-medium">{day.day_name}</td>
                              <td className="py-2 px-3 text-muted-foreground">{day.date}</td>
                              <td className="py-2 px-3 text-green-500"><CurrencyDisplay amount={day.cash_total} /></td>
                              <td className="py-2 px-3 text-blue-500"><CurrencyDisplay amount={day.card_total} /></td>
                              <td className="py-2 px-3 font-bold"><CurrencyDisplay amount={day.total} /></td>
                              <td className="py-2 px-3">{day.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Monthly Report - Weekly Breakdown */}
              {reportType === 'monthly' && reportData.weeks && reportMode === 'detailed' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 print:border-gray-300">
                        <th className="text-right py-2 px-3">{language === 'ar' ? 'الأسبوع' : 'Week'}</th>
                        <th className="text-right py-2 px-3">{language === 'ar' ? 'كاش' : 'Cash'}</th>
                        <th className="text-right py-2 px-3">{language === 'ar' ? 'مكينة' : 'Card'}</th>
                        <th className="text-right py-2 px-3">{language === 'ar' ? 'الإجمالي' : 'Total'}</th>
                        <th className="text-right py-2 px-3">{language === 'ar' ? 'الخدمات' : 'Services'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.weeks.map((week, index) => (
                        <tr key={`week-${week.week_start}-${week.week_end}`} className="border-b border-white/5 print:border-gray-200">
                          <td className="py-2 px-3">
                            {week.week_start} → {week.week_end}
                          </td>
                          <td className="py-2 px-3 text-green-500">
                            <CurrencyDisplay amount={week.cash_total} />
                          </td>
                          <td className="py-2 px-3 text-blue-500">
                            <CurrencyDisplay amount={week.card_total} />
                          </td>
                          <td className="py-2 px-3 font-semibold">
                            <CurrencyDisplay amount={week.total} />
                          </td>
                          <td className="py-2 px-3">{week.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Salary Info (Individual Barber) - Only if user has permission */}
              {reportType === 'monthly' && reportData.salary_info && reportData.can_view_salaries && (
                <div className="mt-6 p-4 bg-purple-500/10 rounded-lg print:border print:border-purple-300">
                  <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-purple-500" />
                    {language === 'ar' ? 'حساب الراتب' : 'Salary Calculation'}
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-sm text-muted-foreground">{language === 'ar' ? 'النسبة' : 'Percentage'}</div>
                      <div className="text-xl font-bold text-purple-500">{reportData.salary_info.percentage}%</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">{language === 'ar' ? 'الراتب' : 'Salary'}</div>
                      <div className="text-xl font-bold text-purple-500">
                        <CurrencyDisplay amount={reportData.salary_info.salary} />
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">{language === 'ar' ? 'صافي للصالون' : 'Net for Salon'}</div>
                      <div className="text-xl font-bold text-primary">
                        <CurrencyDisplay amount={reportData.salary_info.net_after_salary} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Barber Salaries (Salon-wide with Tiers) - Only if user has permission */}
              {reportType === 'monthly' && reportData.barber_salaries && reportData.barber_salaries.length > 0 && reportData.can_view_salaries && (
                <div className="mt-6 p-4 bg-purple-500/10 rounded-lg print:border print:border-purple-300">
                  <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-purple-500" />
                    {language === 'ar' ? 'رواتب الحلاقين (حسب الشرائح)' : 'Barber Salaries (By Tiers)'}
                  </h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-right py-2">{language === 'ar' ? 'الحلاق' : 'Barber'}</th>
                        <th className="text-right py-2">{language === 'ar' ? 'الإيراد' : 'Revenue'}</th>
                        <th className="text-right py-2">{language === 'ar' ? 'الشريحة' : 'Tier'}</th>
                        <th className="text-right py-2">{language === 'ar' ? 'النسبة' : '%'}</th>
                        <th className="text-right py-2">{language === 'ar' ? 'الراتب' : 'Salary'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.barber_salaries.map((bs, idx) => (
                        <tr key={`salary-${bs.barber_id}-${bs.revenue}`} className="border-b border-white/5">
                          <td className="py-2 font-medium">{bs.barber_name}</td>
                          <td className="py-2"><CurrencyDisplay amount={bs.revenue} /></td>
                          <td className="py-2 text-muted-foreground">{bs.tier_name}</td>
                          <td className="py-2">{bs.percentage}%</td>
                          <td className="py-2 font-bold text-purple-500"><CurrencyDisplay amount={bs.salary} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-white/20">
                        <td colSpan="4" className="py-2 font-bold">{language === 'ar' ? 'إجمالي الرواتب' : 'Total Salaries'}</td>
                        <td className="py-2 font-bold text-purple-500">
                          <CurrencyDisplay amount={reportData.barber_salaries.reduce((sum, bs) => sum + bs.salary, 0)} />
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Print Footer */}
              <div className="mt-6 pt-4 border-t border-white/10 print:border-gray-300 text-xs text-muted-foreground">
                <div className="flex justify-between items-center">
                  <div>
                    <p><strong>{language === 'ar' ? 'طُبع بواسطة:' : 'Printed by:'}</strong> {reportData.printed_by || user?.name}</p>
                    <p><strong>{language === 'ar' ? 'تاريخ الطباعة:' : 'Print date:'}</strong> {new Date().toLocaleString(language === 'ar' ? 'ar-AE' : 'en-US')}</p>
                  </div>
                  <div className="text-center">
                    <p>KE Gents Saloon</p>
                    <p className="text-[10px]">{language === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}</p>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>
        )}

        {/* Salary Tiers Modal */}
        <SalaryTiersModal
          open={showTiersModal}
          language={language}
          tiers={salaryTiers}
          newTier={newTier}
          onNewTierChange={setNewTier}
          onAddTier={addTier}
          onRemoveTier={removeTier}
          onSave={saveSalaryTiers}
          onClose={() => setShowTiersModal(false)}
        />
      </div>
    </div>
  );
};

export default DetailedReports;
